'use client';

import { useState, useMemo } from 'react';

import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';

import { clsx } from 'clsx';

import { RealEstateDict } from '@monoimin/intl';

import { Icons, InputLabel } from '@monoimin/atoms';

import { Input } from '@monoimin/molecules/inputs/RealEstateInput';

import { isNonEmptyArray } from '@monoimin/utils/helpers';

import { defineSelectedValueTitle } from '../utils/filters';

import { SelectOption } from './SelectOption';

import type { SelectItem, SelectFilterVariants } from '../types';

export type SelectWithSearchProps<T = SelectItem> = {
  data: Array<T>;
  value: T | Array<T> | undefined;
  onChange: (value: T | Array<T>) => void;
  placeholder: string;
  variant?: SelectFilterVariants;
  searchConfig?: {
    disabled?: boolean;
    placeholder?: string;
  };
  classNames?: {
    container?: string;
  };
  realEstateDict: RealEstateDict;
};

export const SelectWithSearch = <T extends SelectItem>({
  data,
  value,
  onChange,
  placeholder,
  variant,
  searchConfig,
  classNames,
  realEstateDict,
}: SelectWithSearchProps<T>) => {
  const [search, setSearch] = useState('');

  const selectedValueTitle = useMemo(() => {
    return defineSelectedValueTitle(value, placeholder);
  }, [value, placeholder]);

  const selectData = useMemo(() => {
    if (search) {
      return data.filter((item) => {
        if (!item.title) {
          return false;
        }

        return item.title.toLowerCase().includes(search.toLowerCase());
      });
    }

    return data;
  }, [data, search]);

  const haveValue = Boolean(Array.isArray(value) ? value.length : value?.id);

  const isNonEmptySelectData = isNonEmptyArray(selectData);

  return (
    <Listbox
      as='div'
      value={value}
      onChange={onChange}
      multiple
      className={clsx(
        'select body',
        { select_active: haveValue, select_inactive: !haveValue },
        classNames?.container
      )}
    >
      <ListboxButton className='select__button bg-white group'>
        <InputLabel title={placeholder} isActive={haveValue} />
        <span className='select__value whitespace-nowrap truncate'>
          {selectedValueTitle}
        </span>
        <Icons
          icon='dropdown'
          sizeClassName='h-7 w-7'
          className='group-data-[open]:rotate-180 shrink-0'
          aria-hidden
        />
      </ListboxButton>
      <ListboxOptions
        transition
        modal={false}
        className='select__options origin-top transition data-[closed]:scale-95 data-[closed]:opacity-0 duration-100'
      >
        {!searchConfig?.disabled && (
          <Input
            placeholder={searchConfig?.placeholder}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
            className='m-4'
            size='small'
            icon='search'
            autofocus
          />
        )}
        {search && !isNonEmptySelectData ? (
          <div className='px-5 py-2 text-center col-span-full body'>
            <p>{realEstateDict.noSearchResults}</p>
          </div>
        ) : null}
        {isNonEmptySelectData && (
          <div
            className={clsx(
              'max-h-48 overflow-y-scroll custom-scrollbar',
              variant === 'column'
                ? 'flex flex-col'
                : 'flex flex-wrap max-w-xl gap-3 p-4'
            )}
          >
            {selectData.map((item) => (
              <ListboxOption key={item.id} value={item}>
                {({ selected }) => (
                  <SelectOption variant={variant} selected={selected}>
                    {item.title}
                  </SelectOption>
                )}
              </ListboxOption>
            ))}
          </div>
        )}
        {Boolean(!selectData.length && !search) && (
          <div className='p-4'>
            <span>{realEstateDict.noDataForTheFilter}</span>
          </div>
        )}
      </ListboxOptions>
    </Listbox>
  );
};
